import { useState } from "react";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

export function AlertFormDialog({ asset, base = "USD", trigger }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [alertType, setAlertType] = useState("above");
  const [target, setTarget] = useState("");
  const [cooldown, setCooldown] = useState(24);

  const mut = useMutation({
    mutationFn: async () => (await api.post("/alerts", {
      asset_type: asset.asset_type, symbol: asset.symbol, name: asset.name,
      coin_id: asset.coin_id, alert_type: alertType, target_value: parseFloat(target),
      base, cooldown_hours: cooldown,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["alerts"] });
      toast.success(`Alert created for ${asset.symbol}`);
      setOpen(false); setTarget("");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const unit = asset.asset_type === "crypto" ? `${base} price` : `${asset.symbol} per ${base}`;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="rounded-none" data-testid="alert-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Create Alert · {asset.symbol}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Condition</Label>
            <div className="grid grid-cols-3 gap-2">
              {[["above", "Rises above"], ["below", "Falls below"], ["pct_change", "% change ≥"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setAlertType(v)} data-testid={`alert-type-${v}`}
                  className={`border px-2 py-2 text-xs font-medium ${alertType === v ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="target">Target value {alertType === "pct_change" ? "(%)" : `(${unit})`}</Label>
            <Input id="target" type="number" step="any" value={target} onChange={(e) => setTarget(e.target.value)}
              data-testid="alert-target" className="font-mono" placeholder={alertType === "pct_change" ? "10" : "1.50"} />
          </div>
          <div className="space-y-2">
            <Label>Cooldown between notifications</Label>
            <div className="grid grid-cols-4 gap-2">
              {[1, 4, 12, 24].map((h) => (
                <button key={h} type="button" onClick={() => setCooldown(h)} data-testid={`alert-cooldown-${h}`}
                  className={`border px-2 py-2 text-xs font-medium ${cooldown === h ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>
                  {h}h
                </button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">Won't re-notify while price stays past the target; re-arms after it crosses back.</p>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => mut.mutate()} disabled={!target || mut.isPending} className="rounded-none w-full" data-testid="alert-save">
            {mut.isPending ? "Saving…" : "Create Alert"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
